import React from 'react';
import { getTickerUrl } from './chartTheme';

export class ChartErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ChartErrorBoundary render error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            const isDarkMode = document.documentElement.classList.contains('dark');
            return (
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
                    borderRadius: '12px',
                    border: isDarkMode ? '1px solid #374151' : '1px solid #f3f4f6',
                }}>
                    <ChartErrorIcon isDarkMode={isDarkMode} />
                    <p style={{
                        marginTop: '16px',
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                        fontSize: '14px',
                    }}>
                        Widget không khả dụng
                    </p>
                </div>
            );
        }
        return this.props.children;
    }
}

export function ChartErrorIcon({ isDarkMode }) {
    return (
        <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto', display: 'block' }}
        >
            <rect x="4" y="12" width="3" height="8" rx="1" fill={isDarkMode ? '#666' : '#9ca3af'} />
            <rect x="10" y="8" width="3" height="12" rx="1" fill={isDarkMode ? '#666' : '#9ca3af'} />
            <rect x="16" y="4" width="3" height="16" rx="1" fill={isDarkMode ? '#666' : '#9ca3af'} />
            <line
                x1="3" y1="21" x2="21" y2="3"
                stroke={isDarkMode ? '#666' : '#9ca3af'}
                strokeWidth="2"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function TickerInfoCard({ ticker, data, isDarkMode }) {
    if (!data) return null;

    const currentPrice = data.priceClose || data.current_price || 0;
    const priceChange = data.netChange || data.price_change || 0;
    const priceChangePercent = data.pctChange || data.price_change_percent || 0;
    const companyName = data.name || data.nameVi || data.company_name || ticker;
    const isPositive = priceChange >= 0;
    const logoUrl = data.imageUrl || data.logo || `https://ui-avatars.com/api/?name=${ticker}&background=${isDarkMode ? '2a2a2a' : 'e0e0e0'}&color=${isDarkMode ? 'e5e5e5' : '333'}&bold=true&size=32`;
    const tickerUrl = getTickerUrl(ticker);

    return (
        <div
            onClick={() => window.open(tickerUrl, '_blank', 'noopener,noreferrer')}
            style={{
                minWidth: '200px',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDarkMode ? '#2a2a2a' : '#f0f0f0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
            <img
                src={logoUrl}
                alt={ticker}
                style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'contain',
                    backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
                    flexShrink: 0,
                }}
                onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${ticker}&background=${isDarkMode ? '2a2a2a' : 'e0e0e0'}&color=${isDarkMode ? 'e5e5e5' : '333'}&bold=true&size=32`;
                }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{
                    fontSize: '13px',
                    fontWeight: '400',
                    color: isDarkMode ? '#e5e5e5' : '#202124',
                    lineHeight: '1.3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {companyName} ({ticker})
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                        fontSize: '20px',
                        fontWeight: '400',
                        color: isDarkMode ? '#e8eaed' : '#202124',
                        letterSpacing: '-0.01em',
                        lineHeight: '1',
                    }}>
                        {currentPrice ? currentPrice.toLocaleString('vi-VN') : 'N/A'}
                    </span>
                    <span style={{
                        fontSize: '12px',
                        color: isPositive ? '#0F9D58' : '#EA4335',
                        fontWeight: '400',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}>
                        <span>{isPositive ? '▲' : '▼'}</span>
                        <span>{isPositive ? '+' : ''}{priceChangePercent.toFixed(2)}% hôm nay</span>
                    </span>
                </div>
            </div>
        </div>
    );
}

export function TickerInfoSkeleton({ isDarkMode }) {
    return (
        <div style={{
            minWidth: '200px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
        }}>
            <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#2a2a2a' : '#e0e0e0',
                flexShrink: 0,
                animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{
                    width: '120px',
                    height: '13px',
                    backgroundColor: isDarkMode ? '#2a2a2a' : '#e0e0e0',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                }} />
                <div style={{
                    width: '80px',
                    height: '20px',
                    backgroundColor: isDarkMode ? '#2a2a2a' : '#e0e0e0',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                }} />
            </div>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
}

export function TickerInfoHeader({ tickers, tickerInfoData, tickerInfoLoading, isDarkMode }) {
    if (!tickers || tickers.length === 0) return null;

    const validData = tickerInfoData.filter(({ error }) => !error);
    const showSkeleton = tickerInfoLoading && validData.length === 0;
    const showData = validData.length > 0;

    if (!showSkeleton && !showData) return null;

    const items = showSkeleton
        ? tickers.map((ticker) => ({ ticker, component: <TickerInfoSkeleton isDarkMode={isDarkMode} /> }))
        : validData.map(({ ticker, data }) => ({ ticker, component: <TickerInfoCard ticker={ticker} data={data} isDarkMode={isDarkMode} /> }));

    return (
        <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div style={{ display: 'flex', gap: '0px', padding: '16px 20px' }}>
                {items.map(({ ticker, component }, index) => (
                    <React.Fragment key={ticker}>
                        {index > 0 && (
                            <div style={{
                                width: '1px',
                                backgroundColor: isDarkMode ? '#2a2a2a' : '#f0f0f0',
                                margin: '0 8px',
                            }} />
                        )}
                        {component}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
